import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Allows setting a boolean value to true for a certain amount of time.
 *
 * @param timeoutMS Timeout in milliseconds
 */
export const useTimedToggle = (timeoutMS: number = 1000) => {
  const timerRef = useRef<any | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);

  useEffect(() => {
    const startOrStop = () => {
      if (startTime != null) {
        const delta = Date.now() - startTime;
        if (delta >= timeoutMS) {
          setStartTime(null);
        } else {
          timerRef.current = setTimeout(startOrStop, delta);
        }
      }
    };

    startOrStop();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [startTime, timeoutMS]);

  const show = useCallback(() => {
    setStartTime(Date.now());
  }, [setStartTime]);

  const hide = useCallback(() => {
    setStartTime(null);
  }, [setStartTime]);

  const toggle = () => setStartTime((prev) => (prev ? null : Date.now()));

  return {
    value: startTime != null,
    timeout: timeoutMS,
    show,
    hide,
    toggle,
  };
};
